import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BrandImportChangesView } from '@mg/shared';
import { useToast } from '../../../components/ui/toast';
import { ApiRequestError, actionErrorText, api } from '../../../lib/api';
import { useIsoLayoutEffect } from '../../../lib/useIsoLayoutEffect';
import { REREAD_ENABLED } from '../../brand/flags/reread';
import {
  adoptBrandProfile,
  brandKeys,
  IMPORT_POLL_CEILING_MS,
  isImportActive,
  refreshAfterImportStart,
  WIZARD_IMPORT_POLL_MS,
} from '../../brand/queries';
import { useBrandDraft } from '../draft/BrandDraftContext';
import type { BrandDraft } from '../draft/brandDraft';
import {
  editedSinceProposal,
  FIELD_STEPS,
  planBaselineSaves,
  planProposal,
  savedDraft,
  undoDropsEdits,
  type ProposalPlan,
} from './revisitChanges';
import { REVISIT_COPY } from './revisitCopy';
import { registerRevisitStarter, type RereadStartOptions } from './revisitSession';
import { uploadErrorText } from '../step1/fileCheck';

/**
 * Where a re-read is (spec:127-139):
 * - `starting`: Next on revisit step 1 asked for one; the wizard waits on step 1 for the answer.
 * - `running`: the job reads the workspace's stored sources again; it is polled every 2 s, for at most 13
 *   minutes. Every field waits, read-only, so the person's typing never meets the proposal.
 * - `failed`: the job failed, its proposal could not be read, or it ran past the ceiling.
 * - `review`: the proposal is in the draft (nothing saved), with the banner and Show changes.
 * `token` is new for every run and every review: an answer for an older one is ignored, and a review's
 * token is its banner's focus token.
 */
export type RevisitRun =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | {
      kind: 'running';
      token: number;
      jobId: string;
      /** When this run (re)started: a job read before it is not this run's. */
      since: number;
      /** After Regenerate: the revision the new proposal must pass, unless the job was seen running. */
      after: number | null;
      seenActive: boolean;
      /** The job is done and its proposal is being read. */
      loading: boolean;
    }
  | { kind: 'failed'; token: number; jobId: string; message: string; retry: 'job' | 'poll' | null }
  | {
      kind: 'review';
      token: number;
      jobId: string;
      view: BrandImportChangesView;
      /** The saved values with the proposal in (editedSinceProposal compares with it). */
      expected: BrandDraft;
      /** The draft card that shows each proposed audience (card key → its index in the proposal). */
      cards: ReadonlyMap<string, number>;
      /** How many values the proposal changed; 0 shows "nothing to update". */
      changed: number;
      showChanges: boolean;
      /** The banner's close button hid it: Show changes is off and stays off. */
      closed: boolean;
    };

/** What a field draws Show changes from, while it is on. */
export interface RevisitShowing {
  view: BrandImportChangesView;
  cards: ReadonlyMap<string, number>;
}

export interface RevisitContextValue {
  run: RevisitRun;
  /** A re-read is running, or Undo is saving: every field is read-only until it is done. */
  locked: boolean;
  /** Show changes is on: every field is read-only, and a changed one shows its changes. Else null. */
  showing: RevisitShowing | null;
  /** Why Re-read my sources could not start (step 1), until the person moves on or closes it. */
  startError: string | null;
  /** Why Undo, Regenerate or Try again was refused. */
  actionError: string | null;
  /** Undo, Regenerate or Try again is on its way to the server. */
  busy: 'undo' | 'regenerate' | 'retry' | null;
  setShowChanges: (on: boolean) => void;
  /**
   * Undo and restore original text (spec:136, :529): every field back to the baseline the server sent. A step
   * saved since the re-read began is saved back; the banner goes once it is done, and stays if a save fails.
   */
  undo: (token: number) => Promise<void>;
  /** Regenerate (spec:137): the AI reads the same sources again for a new proposal. */
  regenerate: (token: number) => Promise<void>;
  /** Try again after a failure. */
  retry: () => Promise<void>;
  /** The banner's close button. */
  close: () => void;
  dismissStartError: () => void;
  /** A field of steps 2-8 is on the screen: the start error was about step 1, so it goes. */
  fieldShown: () => void;
  /** Whether the person changed anything unsaved since the proposal went in (Regenerate asks first). */
  edited: () => boolean;
  /** Whether Undo would drop something of the person's own, saved or not (it asks first). */
  undoLoses: () => boolean;
}

const RevisitContext = createContext<RevisitContextValue | null>(null);

/** The re-read of the wizard around the caller; null outside one (or while re-reading is switched off). */
export function useRevisit(): RevisitContextValue | null {
  return useContext(RevisitContext);
}

const IDLE: RevisitRun = { kind: 'idle' };

/** Run and review numbers, unique app-wide (a banner's focus token is never an older banner's). */
let lastToken = 0;
const nextToken = () => (lastToken += 1);

function running(jobId: string, after: number | null): RevisitRun {
  return { kind: 'running', token: nextToken(), jobId, since: Date.now(), after, seenActive: false, loading: false };
}

/** A refusal that polling again cannot change (the job is gone, or not this person's to read). */
function isFinalPollError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429;
}

/**
 * "Re-read my sources" in Revisit Brand Setup (spec:127-139, :349; web.md §5.8), around the whole wizard
 * (lane W7; features/brand/flags/reread.ts switches it). It holds the re-read and its review:
 * - Next on revisit step 1 (Re-read my sources) starts the re-read job with no upload (the controller
 *   reaches `start` through revisitSession.ts) and the wizard moves on once the server took it;
 * - when the job is done, its proposal against the saved values (GET …/changes) goes into the draft
 *   through the reducer's own actions (planProposal): only where a field still holds the saved value, never
 *   clearing one, and nothing is saved until the person saves the step;
 * - the review: Show changes on by default (every field read-only, changed ones marked), Undo and restore
 *   original text (every field back to the baseline the server sent, saving back any step saved since the
 *   re-read began), Regenerate (the same sources read again; the new proposal replaces the old), and the
 *   banner's close button.
 * In first-time setup nothing here starts: it only passes its children through with an idle re-read.
 */
export function RevisitProvider({ children }: { children: ReactNode }) {
  if (!REREAD_ENABLED) return <>{children}</>;
  return <ActiveRevisitProvider>{children}</ActiveRevisitProvider>;
}

function ActiveRevisitProvider({ children }: { children: ReactNode }) {
  const { state, dispatch } = useBrandDraft();
  const tenantId = state.tenantId;
  const queryClient = useQueryClient();
  const toast = useToast();

  const [run, setRun] = useState<RevisitRun>(IDLE);
  const [startError, setStartError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<RevisitContextValue['busy']>(null);

  // The run as the last change left it (ahead of the render that shows it), and the draft as it is now:
  // what a handler or an answer that comes back after an await reads.
  const runRef = useRef<RevisitRun>(IDLE);
  const busyRef = useRef(false);
  const draftRef = useRef<BrandDraft>(state);
  // The saved values when the re-read began: what Undo tells a save since from, and each value's tags then,
  // which the baseline does not carry. It outlasts Regenerate, which keeps the job and its baseline.
  const startedRef = useRef<BrandDraft | null>(null);
  useIsoLayoutEffect(() => {
    draftRef.current = state;
  });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const to = useCallback((next: RevisitRun) => {
    runRef.current = next;
    setRun(next);
  }, []);

  /** Every unsaved change goes, the proposal's included: each field back to its saved value. */
  const discardAll = useCallback(() => {
    for (const step of FIELD_STEPS) dispatch({ type: 'discardStep', step });
  }, [dispatch]);

  // ── Starting (Next on revisit step 1) ──
  const start = useCallback(async ({ sources, onProgress }: RereadStartOptions = {}) => {
    const current = runRef.current;
    // One re-read per visit at a time: moving on again from step 1 keeps the one there is.
    if (current.kind === 'starting' || current.kind === 'running' || current.kind === 'review') return;
    setStartError(null);
    setActionError(null);
    to({ kind: 'starting' });
    startedRef.current = savedDraft(draftRef.current);
    let jobId: string;
    try {
      ({ jobId } = await api.startBrandReread(tenantId, sources, { onProgress }));
    } catch (err) {
      if (alive.current) {
        to(IDLE);
        // A refused upload in the document box's words (413 as the size rule), like setup's.
        setStartError(sources?.file ? uploadErrorText(err) : actionErrorText(err, REVISIT_COPY.startFailed));
      }
      throw err;
    }
    if (!alive.current) return;
    void refreshAfterImportStart(queryClient, tenantId);
    to(running(jobId, null));
  }, [tenantId, queryClient, to]);

  useEffect(() => registerRevisitStarter({ start }), [start]);

  // ── The proposal: read it, then put it into the draft ──
  const loadChanges = useCallback(
    async (token: number, jobId: string, revision: number) => {
      const isThisRun = () => {
        const current = runRef.current;
        return alive.current && current.kind === 'running' && current.token === token;
      };
      let view: BrandImportChangesView;
      try {
        view = await api.getBrandImportChanges(tenantId, jobId);
        // Built from an older revision than the job's (the proposal was still being written): ask again.
        if (view.draftRevision < revision) view = await api.getBrandImportChanges(tenantId, jobId);
      } catch (err) {
        if (isThisRun()) {
          to({ kind: 'failed', token, jobId, message: actionErrorText(err, REVISIT_COPY.changesFailed), retry: 'poll' });
        }
        return;
      }
      if (!isThisRun()) return;
      let plan: ProposalPlan;
      try {
        plan = planProposal(draftRef.current, view);
      } catch {
        // Not a proposal Show changes can read: nothing goes into the draft.
        to({ kind: 'failed', token, jobId, message: REVISIT_COPY.changesFailed, retry: 'poll' });
        return;
      }
      for (const action of plan.actions) dispatch(action);
      to({
        kind: 'review',
        token: nextToken(),
        jobId,
        view,
        expected: plan.expected,
        cards: plan.cards,
        changed: plan.changed,
        // Show changes is on by default (spec:133); with nothing changed there is nothing to show.
        showChanges: plan.changed > 0,
        closed: false,
      });
    },
    [tenantId, dispatch, to],
  );

  // ── Polling the job while it runs ──
  const pollJobId = run.kind === 'running' ? run.jobId : null;
  const job = useQuery({
    queryKey: brandKeys.importJob(tenantId, pollJobId ?? ''),
    queryFn: ({ signal }) => api.getBrandImportJob(tenantId, pollJobId as string, signal),
    enabled: pollJobId !== null,
    refetchInterval: pollJobId !== null ? WIZARD_IMPORT_POLL_MS : false,
  });

  useEffect(() => {
    const current = runRef.current;
    if (current.kind !== 'running' || current.loading) return;
    const data = job.data;
    if (!data || data.id !== current.jobId || job.dataUpdatedAt < current.since) return;
    if (isImportActive(data)) {
      if (!current.seenActive) to({ ...current, seenActive: true });
      return;
    }
    if (data.status === 'failed') {
      to({
        kind: 'failed',
        token: current.token,
        jobId: current.jobId,
        message: data.error?.message ?? REVISIT_COPY.failed,
        retry: data.error?.canRetry ? 'job' : null,
      });
      return;
    }
    // Ready (or partial). Right after Regenerate the job may still show the proposal from before it.
    if (!current.seenActive && current.after !== null && data.draftRevision <= current.after) return;
    to({ ...current, loading: true });
    void loadChanges(current.token, current.jobId, data.draftRevision);
  }, [run, job.data, job.dataUpdatedAt, to, loadChanges]);

  useEffect(() => {
    const current = runRef.current;
    if (current.kind !== 'running' || !job.error || job.errorUpdatedAt < current.since) return;
    if (!isFinalPollError(job.error)) return;
    to({
      kind: 'failed',
      token: current.token,
      jobId: current.jobId,
      message: actionErrorText(job.error, REVISIT_COPY.failed),
      retry: null,
    });
  }, [run, job.error, job.errorUpdatedAt, to]);

  // The client's ceiling (13 minutes, past the server's own deadline and its stale rule).
  const runningToken = run.kind === 'running' ? run.token : null;
  useEffect(() => {
    if (runningToken === null) return;
    const timer = setTimeout(() => {
      const current = runRef.current;
      if (current.kind === 'running' && current.token === runningToken) {
        to({ kind: 'failed', token: current.token, jobId: current.jobId, message: REVISIT_COPY.timedOut, retry: 'poll' });
      }
    }, IMPORT_POLL_CEILING_MS);
    return () => clearTimeout(timer);
  }, [runningToken, to]);

  // ── The review ──
  const setShowChanges = useCallback(
    (on: boolean) => {
      const current = runRef.current;
      if (current.kind === 'review' && !current.closed && current.showChanges !== on) to({ ...current, showChanges: on });
    },
    [to],
  );

  const undo = useCallback(
    async (token: number) => {
      const current = runRef.current;
      if (current.kind !== 'review' || current.token !== token || busyRef.current) return;
      setActionError(null);
      const saves = planBaselineSaves(draftRef.current, current.view, startedRef.current);
      if (saves.length > 0) {
        busyRef.current = true;
        setBusy('undo');
        // Each step on its own, so one that is refused leaves the others back; Undo again finishes the rest.
        let version = draftRef.current.version;
        let failure: unknown = null;
        for (const { step, ...restored } of saves) {
          try {
            const view = await api.saveOnboardingStep(tenantId, step, { ...restored, expectedVersion: version, resumeAt: step });
            adoptBrandProfile(queryClient, view);
            dispatch({ type: 'stepSaved', step, view });
            version = view.version;
          } catch (err) {
            failure ??= err;
          }
        }
        busyRef.current = false;
        if (alive.current) setBusy(null);
        if (failure !== null) {
          if (alive.current) setActionError(actionErrorText(failure, REVISIT_COPY.undoFailed));
          return;
        }
        const still = runRef.current;
        if (!alive.current || still.kind !== 'review' || still.token !== token) return;
        toast.show(REVISIT_COPY.restored);
      }
      discardAll();
      to(IDLE);
    },
    [tenantId, queryClient, dispatch, discardAll, to, toast],
  );

  const regenerate = useCallback(
    async (token: number) => {
      const current = runRef.current;
      if (current.kind !== 'review' || current.token !== token || busyRef.current) return;
      busyRef.current = true;
      setBusy('regenerate');
      setActionError(null);
      try {
        await api.regenerateBrandReread(tenantId, current.jobId);
      } catch (err) {
        if (alive.current) setActionError(actionErrorText(err, REVISIT_COPY.regenerateFailed));
        return;
      } finally {
        busyRef.current = false;
        if (alive.current) setBusy(null);
      }
      const still = runRef.current;
      if (!alive.current || still.kind !== 'review' || still.token !== token) return;
      // The old proposal goes (with anything changed since, which the banner asked about), so the new one
      // is compared with, and goes in over, the saved values when it arrives.
      discardAll();
      void refreshAfterImportStart(queryClient, tenantId);
      to(running(current.jobId, current.view.draftRevision));
    },
    [tenantId, queryClient, discardAll, to],
  );

  const retry = useCallback(async () => {
    const current = runRef.current;
    if (current.kind !== 'failed' || busyRef.current) return;
    setActionError(null);
    if (current.retry === 'poll') {
      // Look again: the job may have finished, or its proposal may be readable now.
      to(running(current.jobId, null));
      return;
    }
    if (current.retry !== 'job') return;
    busyRef.current = true;
    setBusy('retry');
    let jobId: string;
    try {
      ({ jobId } = await api.retryBrandImport(tenantId, current.jobId));
    } catch (err) {
      if (alive.current) setActionError(actionErrorText(err, REVISIT_COPY.retryFailed));
      return;
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(null);
    }
    const still = runRef.current;
    if (!alive.current || still.kind !== 'failed' || still.token !== current.token) return;
    void refreshAfterImportStart(queryClient, tenantId);
    to(running(jobId, null));
  }, [tenantId, queryClient, to]);

  const close = useCallback(() => {
    const current = runRef.current;
    if (busyRef.current) return;
    setActionError(null);
    if (current.kind === 'review') to({ ...current, closed: true, showChanges: false });
    else if (current.kind === 'failed') to(IDLE);
  }, [to]);

  const dismissStartError = useCallback(() => setStartError(null), []);
  const fieldShown = useCallback(() => setStartError(null), []);
  const edited = useCallback(() => {
    const current = runRef.current;
    return current.kind === 'review' && editedSinceProposal(draftRef.current, current.expected);
  }, []);
  const undoLoses = useCallback(() => {
    const current = runRef.current;
    return current.kind === 'review' && undoDropsEdits(draftRef.current, current.expected, current.view, startedRef.current);
  }, []);

  const value = useMemo<RevisitContextValue>(
    () => ({
      run,
      locked: run.kind === 'running' || busy === 'undo',
      showing:
        run.kind === 'review' && run.showChanges && !run.closed ? { view: run.view, cards: run.cards } : null,
      startError,
      actionError,
      busy,
      setShowChanges,
      undo,
      regenerate,
      retry,
      close,
      dismissStartError,
      fieldShown,
      edited,
      undoLoses,
    }),
    [run, startError, actionError, busy, setShowChanges, undo, regenerate, retry, close, dismissStartError, fieldShown, edited, undoLoses],
  );

  return <RevisitContext.Provider value={value}>{children}</RevisitContext.Provider>;
}
